import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  CoverageAnalyticsSummary, CoverageAssignmentResponse, CoverageChapterResponse,
  CoverageEventResponse, CoverageFeedResponse, CoverageGeographyResponse,
  CoverageMarkResponse, CoverageMarkSummaryRow, CoverageRecordingResponse,
  CoverageSubjectResponse, CoverageSubjectTypeOption, CoverageWatcherRow,
  FeedTokenResponse, LiveFeedResponse, OfflineRecordingResponse, PageResponse,
  PlaybackResponse,
} from '@/shared/api/types'
import type { CapturedLocation } from '@/shared/geo/geolocation'

/**
 * Live coverage — cameras on the campaign trail.
 *
 * ## Four things about this API that are not obvious from the shapes
 *
 * **1. Nothing here carries media.** The backend mints a token and receives
 * webhooks; video goes browser ↔ LiveKit and never through `/api/v1`. So a
 * player is not "an endpoint that returns a stream" — it is `POST .../token`
 * followed by a WebRTC connection to `wsUrl`, and the two failure modes are
 * completely different. A 403 from the token call is an authorisation problem;
 * a connection that never reaches `connected` is a network one, and the UI must
 * not report the second as the first.
 *
 * **2. A hidden event is a 404, not a 403.** Classification is enforced
 * server-side and the refusal is deliberately indistinguishable from absence —
 * a 403 would confirm that a TOP_SECRET event exists at that id. So the client
 * must never render "you do not have permission to view this event": it does
 * not know that, and saying it leaks the same fact the backend refused to.
 *
 * **3. Watch time is measured, never asserted.** There is no endpoint that
 * accepts a duration. `coverage_view` rows are opened and closed by LiveKit
 * webhooks. Do not add a heartbeat that posts seconds watched — the whole point
 * of the design is that the number is not the browser's to supply.
 *
 * **4. `replayMinutes` is null and stays null.** See its javadoc in `types.ts`.
 */

// ── the diary ───────────────────────────────────────────────────────────────

export const listCoverageEvents = (params: Record<string, QueryValue>, page = 0, size = 50) =>
  api.get<PageResponse<CoverageEventResponse>>(`/coverage/events${qs({ ...params, page, size })}`)

export const getCoverageEvent = (id: string) =>
  api.get<CoverageEventResponse>(`/coverage/events/${id}`)

export const createCoverageEvent = (body: unknown) =>
  api.post<CoverageEventResponse>('/coverage/events', body)

export const updateCoverageEvent = (id: string, body: unknown) =>
  api.patch<CoverageEventResponse>(`/coverage/events/${id}`, body)

/** SCHEDULED → CANCELLED. Refused while any feed is live. */
export const cancelCoverageEvent = (id: string, reason: string) =>
  api.post<CoverageEventResponse>(`/coverage/events/${id}/cancel`, { reason })

/** Ends the event AND deletes the LiveKit room, so a phone that stopped being welcome stops publishing. */
export const endCoverageEvent = (id: string) =>
  api.post<CoverageEventResponse>(`/coverage/events/${id}/end`, {})

/**
 * Undo an End pressed by mistake.
 *
 * Ending was terminal, and it is the one destructive action on this feature that
 * a person performs while something is actually happening — a thumb on the wrong
 * button at a rally ends the coverage and there was no way back. Reopening leaves
 * every recording, view and mark alone: it moves the event's status and nothing
 * else, and it is audited.
 */
export const reopenCoverageEvent = (id: string, reason: string) =>
  api.post<CoverageEventResponse>(`/coverage/events/${id}/reopen`, { reason })

/** Only a SCHEDULED event no feed of which has ever started. Anything that went live is ended, never deleted. */
export const deleteCoverageEvent = (id: string, reason: string) =>
  api.del<void>(`/coverage/events/${id}`, { body: { reason } })

// ── feeds ───────────────────────────────────────────────────────────────────

export const listLiveCoverage = () => api.get<LiveFeedResponse[]>('/coverage/live')

/** What was said at an event, merged across its cameras and in spoken order. */
export interface CoverageTranscriptLine {
  /** The FEED's name, never a person — an event's camera does not know who spoke. */
  camera: string
  feedId: string | null
  recordingId: string
  at: string
  startMs: number
  endMs: number
  text: string
}

export interface CoverageTranscript {
  /** NONE | NO_RECORDING | PENDING | SKIPPED | FAILED | EMPTY | READY */
  stateCode: string
  lines: CoverageTranscriptLine[]
  language: string | null
  /**
   * Why there are no words, when the engine said. A rally in Kalenjin lands here: the
   * transcriber has no model for it and will not guess.
   */
  reason: string | null
}

export const coverageTranscript = (eventId: string) =>
  api.get<CoverageTranscript>(`/coverage/events/${eventId}/transcript`)

export const createCoverageFeed = (eventId: string, body: unknown) =>
  api.post<CoverageFeedResponse>(`/coverage/events/${eventId}/feeds`, body)

export const deleteCoverageFeed = (id: string) =>
  api.del<void>(`/coverage/feeds/${id}`)

/**
 * The credential. `role` decides which permission the server demands, which is
 * why the controller cannot express it in `@PreAuthorize` and neither can we.
 *
 * A refusal here is never re-worded by the client. The server's detail strings
 * ("Another account is assigned to this camera.", "This event is over.") say
 * exactly what is wrong, and paraphrasing them is how a UI ends up telling
 * somebody they lack a permission they hold.
 */
export const mintFeedToken = (
  feedId: string,
  role: 'PUBLISHER' | 'VIEWER',
  opts: {
    deviceLabel?: string
    /**
     * Where the DEVICE is, captured as it goes on air — not where the event was
     * planned. Sent only for `PUBLISHER`; a viewer's location is none of the
     * platform's business.
     *
     * Exactly one of `location` or `locationUnavailableReason` is sent. See
     * `geolocation.ts` for why "required" cannot mean "blocked": a refused
     * permission must produce a recorded reason, not an unfilmed rally and not a
     * coordinate somebody typed in to get past the form.
     */
    location?: CapturedLocation
    locationUnavailableReason?: string
  } = {},
) => api.post<FeedTokenResponse>(`/coverage/feeds/${feedId}/token`, {
  role,
  deviceLabel: opts.deviceLabel,
  latitude: opts.location?.latitude,
  longitude: opts.location?.longitude,
  locationAccuracyM: opts.location?.accuracyM,
  locatedAt: opts.location?.capturedAt,
  locationUnavailableReason: opts.locationUnavailableReason,
})

export const forceEndFeed = (feedId: string, reason: string) =>
  api.post<CoverageFeedResponse>(`/coverage/feeds/${feedId}/force-end`, { reason })

// ── recordings ──────────────────────────────────────────────────────────────

export const listCoverageRecordings = (eventId: string) =>
  api.get<CoverageRecordingResponse[]>(`/coverage/events/${eventId}/recordings`)

/** A 15-minute presigned URL. Short on purpose: a link pasted into a chat expires before it is useful. */
export const recordingPlayback = (id: string) =>
  api.get<PlaybackResponse>(`/coverage/recordings/${id}/playback`)

/**
 * The poster frame. A 60-minute presign, deliberately longer than playback's
 * fifteen: a board re-signing forty thumbnails on every poll is a board that
 * spends its time signing URLs, and a still frame is not the footage.
 */
export const recordingPoster = (id: string) =>
  api.get<PlaybackResponse>(`/coverage/recordings/${id}/poster`)

export const reserveOfflineRecording = (eventId: string, body: unknown) =>
  api.post<OfflineRecordingResponse>(`/coverage/events/${eventId}/recordings/offline`, body)

export const completeOfflineRecording = (id: string) =>
  api.post<CoverageRecordingResponse>(`/coverage/recordings/${id}/complete`, {})

export const deleteCoverageRecording = (id: string, reason: string) =>
  api.del<void>(`/coverage/recordings/${id}`, { body: { reason } })

// ── assignments ─────────────────────────────────────────────────────────────

export const listEventAssignments = (eventId: string) =>
  api.get<CoverageAssignmentResponse[]>(`/coverage/events/${eventId}/assignments`)

export const listMyAssignments = () =>
  api.get<CoverageAssignmentResponse[]>('/coverage/assignments/mine')

export const createAssignment = (eventId: string, body: unknown) =>
  api.post<CoverageAssignmentResponse>(`/coverage/events/${eventId}/assignments`, body)

/** WITHDRAWN, never a row delete: an assignment that was made and pulled is a fact. */
export const withdrawAssignment = (id: string) =>
  api.del<void>(`/coverage/assignments/${id}`)

export const acceptAssignment = (id: string) =>
  api.post<CoverageAssignmentResponse>(`/coverage/assignments/${id}/accept`, {})

export const declineAssignment = (id: string, reason: string) =>
  api.post<CoverageAssignmentResponse>(`/coverage/assignments/${id}/decline`, { reason })

// ── marks ───────────────────────────────────────────────────────────────────

export const listEventMarks = (eventId: string) =>
  api.get<CoverageMarkResponse[]>(`/coverage/events/${eventId}/marks`)

export const createMark = (eventId: string, body: unknown) =>
  api.post<CoverageMarkResponse>(`/coverage/events/${eventId}/marks`, body)

export const deleteMark = (id: string) => api.del<void>(`/coverage/marks/${id}`)

/** What the replay player draws on the scrub bar. Only marks that fell inside the recording. */
export const recordingChapters = (recordingId: string) =>
  api.get<CoverageChapterResponse[]>(`/coverage/recordings/${recordingId}/chapters`)

// ── watches ─────────────────────────────────────────────────────────────────

export const watchEvent = (eventId: string) =>
  api.post<void>(`/coverage/events/${eventId}/watch`, {})

export const unwatchEvent = (eventId: string) =>
  api.del<void>(`/coverage/events/${eventId}/watch`)

// ── subject attachment ──────────────────────────────────────────────────────

/**
 * What THIS deployment can attach coverage to. Built from the resolvers on the
 * classpath, so the picker is right on the day `survey` ships and wrong on no day
 * — which a hardcoded list could not manage.
 */
export const listSubjectTypes = () =>
  api.get<CoverageSubjectTypeOption[]>('/coverage/subject-types')

export const searchSubjects = (code: string, q: string) =>
  api.get<CoverageSubjectResponse[]>(`/coverage/subject-types/${encodeURIComponent(code)}/search${qs({ q })}`)

// ── analytics ───────────────────────────────────────────────────────────────

export const coverageSummary = (params: Record<string, QueryValue>) =>
  api.get<CoverageAnalyticsSummary>(`/coverage/analytics/summary${qs(params)}`)

export const coverageGeography = (params: Record<string, QueryValue>) =>
  api.get<CoverageGeographyResponse>(`/coverage/analytics/geography${qs(params)}`)

export const coverageWatchers = (params: Record<string, QueryValue>) =>
  api.get<CoverageWatcherRow[]>(`/coverage/analytics/watchers${qs(params)}`)

export const coverageMarkSummary = (params: Record<string, QueryValue>) =>
  api.get<CoverageMarkSummaryRow[]>(`/coverage/analytics/marks${qs(params)}`)
